import {
  AssessmentPublishingStatus,
  HomeAssessmentTableRow,
  StudentHomeAssessmentTableRow,
} from "api/api-types-assessment";
import type { CourseType, SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { ORDER_BY_TITLE } from "mds/hooks/use-ordered";

export type HomeAssessmentSection = {
  id: string;
  title: string;
  code_title: string;
  pages: HomeAssessmentTableRow[];
};

export type StudentHomeAssessmentSection = {
  id: string;
  title: string;
  code_title: string;
  pages: StudentHomeAssessmentTableRow[];
};

export const getHomeAssessmentPublishLabel = (
  publishing: AssessmentPublishingStatus,
  isAssessmentComplete: boolean,
  isMarkedComplete: boolean,
): string => {
  if (publishing === AssessmentPublishingStatus.draft) {
    return t("publishing.unpublished");
  }

  if (isAssessmentComplete || isMarkedComplete) {
    return t("publishing.published");
  }

  return t("publishing.partial");
};

export const buildHomeAssessmentSections = (
  rows: HomeAssessmentTableRow[],
  sectionsForTerm: Pick<SectionType, "id" | "title" | "course_id">[],
  coursesById: Record<string, CourseType | undefined>,
): HomeAssessmentSection[] => {
  const pagesBySectionId = new Map<string, HomeAssessmentTableRow[]>();
  const sectionOrder: string[] = [];

  for (const row of rows) {
    if (!pagesBySectionId.has(row.section_id)) {
      sectionOrder.push(row.section_id);
      pagesBySectionId.set(row.section_id, []);
    }
    pagesBySectionId.get(row.section_id)!.push(row);
  }

  const nonEmptySections = sectionOrder.map((sectionId) => {
    const sectionRows = pagesBySectionId.get(sectionId)!;
    const firstRow = sectionRows[0];
    return {
      id: sectionId,
      title: firstRow.section_title,
      code_title: firstRow.code_title,
      pages: sectionRows,
    };
  });

  const sectionIdsWithPages = new Set(sectionOrder);
  const emptySections = sectionsForTerm
    .filter((section) => !sectionIdsWithPages.has(section.id))
    .toSorted(ORDER_BY_TITLE)
    .map((section) => ({
      id: section.id,
      title: section.title,
      code_title: coursesById[section.course_id]?.code ?? "",
      pages: [] as HomeAssessmentTableRow[],
    }));

  return [...nonEmptySections, ...emptySections];
};

export const buildStudentHomeAssessmentSections = (
  rows: StudentHomeAssessmentTableRow[],
  sectionsForTerm: Pick<SectionType, "id" | "title" | "course_id">[],
  coursesById: Record<string, CourseType | undefined>,
): StudentHomeAssessmentSection[] => {
  const pagesBySectionId = new Map<string, StudentHomeAssessmentTableRow[]>();
  const sectionOrder: string[] = [];

  for (const row of rows) {
    if (!pagesBySectionId.has(row.section_id)) {
      sectionOrder.push(row.section_id);
      pagesBySectionId.set(row.section_id, []);
    }
    pagesBySectionId.get(row.section_id)!.push(row);
  }

  const nonEmptySections = sectionOrder.map((sectionId) => {
    const sectionRows = pagesBySectionId.get(sectionId)!;
    const firstRow = sectionRows[0];
    return {
      id: sectionId,
      title: firstRow.section_title,
      code_title: firstRow.course_code,
      pages: sectionRows,
    };
  });

  const sectionIdsWithPages = new Set(sectionOrder);
  const emptySections = sectionsForTerm
    .filter((section) => !sectionIdsWithPages.has(section.id))
    .toSorted(ORDER_BY_TITLE)
    .map((section) => ({
      id: section.id,
      title: section.title,
      code_title: coursesById[section.course_id]?.code ?? "",
      pages: [] as StudentHomeAssessmentTableRow[],
    }));

  return [...nonEmptySections, ...emptySections];
};
